import React, { useEffect, useRef } from 'react';
import { Box, VStack, Text, Skeleton } from '@chakra-ui/react';
import { QRDisplayIcon } from '../../../../components/payments/payment-icons';
import QRCode from 'qrcode';
import { useQRData } from '@/hooks/payments/useQRData';
import { useBalance } from '@/hooks/wallet/useBalance';

export const MyQRContent: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { data: qrData, isLoading: isLoadingQR, error: qrError } = useQRData();
  const { data: balanceData, isLoading: isLoadingBalance } = useBalance();

  const loading = isLoadingQR || isLoadingBalance;
  const error = qrError;

  // QRデータが取得できたらcanvasに描画
  useEffect(() => {
    if (!qrData || !canvasRef.current) {
      return;
    }

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    console.log('[MyQRContent] Generating QR code on canvas');
    QRCode.toCanvas(canvas, qrData.qrCodeUrl, {
      width: 260,
      margin: 2,
      errorCorrectionLevel: 'H', // 高いエラー訂正レベル(最大30%の損傷に耐える)
      color: {
        dark: '#000000',
        light: '#FFFFFF',
      },
    })
      .then(() => {
        console.log('[MyQRContent] QR code generated successfully');

        // QRコードの中央にロゴを配置
        const logo = new Image();
        logo.onload = () => {
          const logoSize = 50; // ロゴのサイズ
          const x = (canvas.width - logoSize) / 2;
          const y = (canvas.height - logoSize) / 2;

          // ロゴの背景として白い丸を描画
          ctx.fillStyle = '#FFFFFF';
          ctx.beginPath();
          ctx.arc(canvas.width / 2, canvas.height / 2, logoSize / 2 + 4, 0, 2 * Math.PI);
          ctx.fill();

          // ロゴを描画
          ctx.drawImage(logo, x, y, logoSize, logoSize);
        };
        logo.onerror = err => {
          console.error('[MyQRContent] Logo image failed to load:', err);
        };
        logo.src = '/img/BunPay-logo-sample4.png';
      })
      .catch(err => {
        console.error('[MyQRContent] QR code generation failed:', err);
      });
  }, [qrData]);

  return (
    <VStack gap={4} justify="center" align="center" userSelect="none">
      {/* QRコード表示エリア */}
      <Box
        w="full"
        maxW="300px"
        h="300px"
        bg="white"
        border="2px solid"
        borderColor="gray.200"
        borderRadius="xl"
        display="flex"
        alignItems="center"
        justifyContent="center"
        position="relative"
        boxShadow="lg"
      >
        {loading ? (
          <Skeleton w="260px" h="260px" borderRadius="lg" />
        ) : error || !qrData ? (
          <Box
            w="260px"
            h="260px"
            bg="gray.100"
            borderRadius="lg"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <QRDisplayIcon boxSize={24} color="gray.400" />
          </Box>
        ) : (
          <canvas
            ref={canvasRef}
            style={{
              borderRadius: '8px',
              maxWidth: '260px',
              maxHeight: '260px',
            }}
          />
        )}
      </Box>

      <VStack gap={2} mt={4}>
        <Box display="flex" alignItems="center" gap={2}>
          <Text fontSize="lg" fontWeight="semibold" color="gray.800">
            残高:
          </Text>
          <Skeleton height="28px" width="80px" borderRadius="md" loading={isLoadingBalance}>
            <Text fontSize="lg" fontWeight="semibold" color="gray.800">
              ฿{balanceData?.balance.toLocaleString() ?? '---'}
            </Text>
          </Skeleton>
        </Box>
        <Text fontSize="sm" color="gray.600" textAlign="center">
          このQRコードを店舗スタッフに
          <br />
          読み取ってもらってください
        </Text>
        {error && (
          <Text fontSize="xs" color="red.500" mt={2}>
            QRコードの読み込みに失敗しました
          </Text>
        )}
      </VStack>
    </VStack>
  );
};
